import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Sheet, Stack, Tooltip, 
    Card, CardContent, Chip, Select, Option,
    useColorScheme, List, ListItem, ListItemContent,
    Grid
} from '@mui/joy';
import useMediaQuery from '@mui/material/useMediaQuery';
import { theme } from "../App";
import { APIGetActivityHeatmap } from '../api';


const ActivityHeatmap = ({ token }) => {
    const { mode } = useColorScheme();
    const [heatmapData, setHeatmapData] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [availableYears, setAvailableYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(true);
    const [hoveredDay, setHoveredDay] = useState(null);
    const [selectedDay, setSelectedDay] = useState(null);

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        loadHeatmapData(selectedYear);
    }, [selectedYear]);

    const loadHeatmapData = async (year) => {
        try {
            setLoading(true);
            const response = await APIGetActivityHeatmap(token, year);
            if (response.success) {
                setHeatmapData(response.heatmap_data);
                setStatistics(response.statistics);
                setAvailableYears(response.available_years || []);
            }
        } catch (error) {
            console.error('Error loading heatmap data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleYearChange = (event, newValue) => {
        setSelectedYear(parseInt(newValue));
        setSelectedDay(null);
    };

    const handleDayClick = (day) => {
        setSelectedDay(day);
    };

    // Функция группировки для десктопной версии (горизонтальная)
    const getGroupedDataDesktop = () => {
        if (!heatmapData.length) return [];

        const months = [];
        let currentMonth = null;
        let currentWeek = [];
        
        // Сортируем данные по дате (январь -> декабрь)
        const sortedData = [...heatmapData].sort((a, b) => new Date(a.date) - new Date(b.date));
        
        sortedData.forEach((day, index) => {
            const date = new Date(day.date);
            const month = date.toLocaleString('ru-RU', { month: 'short' });
            const dayOfWeek = date.getDay();
            let normalizedDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // 0=понедельник, 6=воскресенье
            
            if (month !== currentMonth) {
                if (currentWeek.length > 0 && currentMonth) {
                    // Дополняем неделю пустыми днями до воскресенья
                    while (currentWeek.length < 7) {
                        currentWeek.push(null);
                    }
                    months[months.length - 1].weeks.push([...currentWeek]);
                    currentWeek = [];
                }
                currentMonth = month;
                months.push({
                    name: month,
                    weeks: [],
                    monthNumber: date.getMonth() // Для сортировки
                });
            }
            
            // Если это понедельник и текущая неделя не пустая
            if (normalizedDayOfWeek === 0 && currentWeek.length > 0) {
                // Дополняем предыдущую неделю пустыми днями до воскресенья
                while (currentWeek.length < 7) {
                    currentWeek.push(null);
                }
                months[months.length - 1].weeks.push([...currentWeek]);
                currentWeek = [];
            }
            
            // Добавляем пустые дни в начале недели, если нужно
            while (currentWeek.length < normalizedDayOfWeek) {
                currentWeek.push(null);
            }
            
            currentWeek.push(day);
            
            // Последний день - завершаем текущую неделю
            if (index === sortedData.length - 1) {
                while (currentWeek.length < 7) {
                    currentWeek.push(null);
                }
                months[months.length - 1].weeks.push([...currentWeek]);
            }
        });
        
        // Сортируем месяцы по номеру (январь -> декабрь)
        return months.sort((a, b) => a.monthNumber - b.monthNumber);
    };

    // Функция группировки для мобильной версии (вертикальная)
    const getGroupedDataMobile = () => {
        if (!heatmapData.length) return [];

        const months = [];
        let currentMonth = null;
        let currentWeek = [];
        
        // Сортируем данные по дате
        const sortedData = [...heatmapData].sort((a, b) => new Date(a.date) - new Date(b.date));
        
        sortedData.forEach((day, index) => {
            const date = new Date(day.date);
            const month = date.toLocaleString('ru-RU', { month: 'short' });
            const dayOfWeek = date.getDay();
            const normalizedDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // 0=понедельник, 6=воскресенье
            
            if (month !== currentMonth) {
                if (currentWeek.length > 0 && currentMonth) {
                    // Дополняем неделю пустыми днями до воскресенья
                    while (currentWeek.length < 7) {
                        currentWeek.push(null);
                    }
                    months[months.length - 1].weeks.push([...currentWeek]);
                    currentWeek = [];
                }
                currentMonth = month;
                months.push({
                    name: month,
                    weeks: [],
                    monthNumber: date.getMonth(),
                    year: date.getFullYear()
                });
            }
            
            // Если это понедельник и текущая неделя не пустая
            if (normalizedDayOfWeek === 0 && currentWeek.length > 0) {
                // Дополняем предыдущую неделю пустыми днями до воскресенья
                while (currentWeek.length < 7) {
                    currentWeek.push(null);
                }
                months[months.length - 1].weeks.push([...currentWeek]);
                currentWeek = [];
            }
            
            // Добавляем пустые дни в начале недели, если нужно
            while (currentWeek.length < normalizedDayOfWeek) {
                currentWeek.push(null);
            }
            
            currentWeek.push(day);
            
            // Последний день - завершаем текущую неделю
            if (index === sortedData.length - 1) {
                while (currentWeek.length < 7) {
                    currentWeek.push(null);
                }
                months[months.length - 1].weeks.push([...currentWeek]);
            }
        });
        
        return months.sort((a, b) => a.monthNumber - b.monthNumber);
    };

    const getColorForLevel = (level) => {
        switch (level) {
            case 0: return mode == 'light' ? '#ebedf0' : '#30363d';
            case 1: return mode == 'light' ? '#9be9a8' : '#216e39';
            case 2: return mode == 'light' ? '#40c463' : '#30a14e';
            case 3: return mode == 'light' ? '#30a14e' : '#40c463';
            case 4: return mode == 'light' ? '#216e39' : '#9be9a8';
            default: return mode == 'light' ? '#ebedf0' : '#30363d';
        }
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('ru-RU', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getActivityDescription = (day) => {
        console.log("day.total_activity", day);
        if (!day || day.total_activity === 0) return 'Нет активности';
        
        const parts = [];
        if (day.tests_created > 0) parts.push(`${day.tests_created} тестов создано`);
        if (day.tests_completed > 0) parts.push(`${day.tests_completed} тестов завершено`);
        if (day.questions_answered > 0) parts.push(`${day.questions_answered} вопросов отвечено`);
        
        return parts.join(', ');
    };

    const getWeekdayLabel = (index) => {
        const weekdays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
        return weekdays[index];
    };

    const renderDayTooltip = (day) => (
        <Box sx={{ p: 1, maxWidth: 250 }}>
            <Typography level="body2" fontWeight="bold" sx={{ mb: 1 }}>
                {formatDate(day.date)}
            </Typography>
            <List size="sm" sx={{ '--ListItem-paddingY': '2px' }}>
                <ListItem>
                    <ListItemContent>
                        <Typography level="body3">Общая активность:</Typography>
                        <Typography level="body2" fontWeight="bold">
                            {day.total_activity} действий
                        </Typography>
                    </ListItemContent>
                </ListItem>
                <ListItem>
                    <ListItemContent>
                        <Typography level="body3">Создано тестов:</Typography>
                        <Typography level="body2" color="primary">
                            {day.tests_created}
                        </Typography>
                    </ListItemContent>
                </ListItem>
                <ListItem>
                    <ListItemContent>
                        <Typography level="body3">Завершено тестов:</Typography>
                        <Typography level="body2" color="success">
                            {day.tests_completed}
                        </Typography>
                    </ListItemContent>
                </ListItem>
                <ListItem>
                    <ListItemContent>
                        <Typography level="body3">Отвечено вопросов:</Typography>
                        <Typography level="body2">
                            {day.questions_answered}
                        </Typography>
                    </ListItemContent>
                </ListItem>
                <ListItem>
                    <ListItemContent>
                        <Typography level="body3">Правильных ответов:</Typography>
                        <Typography level="body2" color="success">
                            {day.correct_answers}
                        </Typography>
                    </ListItemContent>
                </ListItem>
                <ListItem>
                    <ListItemContent>
                        <Typography level="body3">Неправильных ответов:</Typography>
                        <Typography level="body2" color="danger">
                            {day.incorrect_answers}
                        </Typography>
                    </ListItemContent>
                </ListItem>
            </List>
        </Box>
    );

    const renderDayCell = (day, size = 12) => {
        if (!day) {
            return (
                <Box
                    sx={{
                        width: size,
                        height: size,
                        borderRadius: '2px',
                        backgroundColor: 'transparent'
                    }}
                />
            );
        }

        return (
            <Tooltip
                title={renderDayTooltip(day)}
                arrow
                placement="top"
                color='primary'
                variant='soft'
            >
                <Box
                    sx={{
                        width: size,
                        height: size,
                        borderRadius: '2px',
                        backgroundColor: getColorForLevel(day.level),
                        border: selectedDay?.date === day.date ? 
                            '2px solid #ff6b35' : 
                            hoveredDay === day.date ? 
                            '2px solid #1a1a1a' : 
                            '1px solid rgba(27,31,35,0.06)',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            border: '2px solid #1a1a1a',
                            transform: 'scale(1.1)'
                        }
                    }}
                    onMouseEnter={() => setHoveredDay(day.date)}
                    onMouseLeave={() => setHoveredDay(null)}
                    onClick={() => handleDayClick(day)}
                />
            </Tooltip>
        );
    };

    // Рендер десктопной версии
    const renderDesktopVersion = () => {
        const groupedData = getGroupedDataDesktop();

        return (
            <Stack direction="row" spacing={1} sx={{ pb: 1 }}>
                {/* Подписи дней недели */}
                <Stack spacing={0.25} sx={{ width: 30, flexShrink: 0, pt: 2.6 }}>
                    {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => (
                        <Typography 
                            key={dayIndex} 
                            level="body-xs" 
                            color="neutral" 
                            sx={{ height: 14, textAlign: 'center' }}
                        >
                            {getWeekdayLabel(dayIndex)}
                        </Typography>
                    ))}
                </Stack>

                {/* Месяцы и ячейки активности */}
                <Stack direction="row" spacing={1}>
                    {groupedData.map((month, monthIndex) => (
                        <Box key={monthIndex}>
                            <Typography 
                                level="body3" 
                                color="neutral" 
                                sx={{ textAlign: 'center', mb: 0.5, height: 20 }}
                            >
                                {month.name}
                            </Typography>
                            
                            <Stack direction="row" spacing={0.5}>
                                {month.weeks.map((week, weekIndex) => (
                                    <Stack key={weekIndex} spacing={0.5}>
                                        {week.map((day, dayIndex) => (
                                            <React.Fragment key={dayIndex}>
                                                {renderDayCell(day, 12)}
                                            </React.Fragment>
                                        ))}
                                    </Stack>
                                ))}
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            </Stack>
        );
    };

    // Рендер детальной информации о выбранном дне
    const renderSelectedDayDetails = () => {
        if (!selectedDay) return null;

        return (
            <Card variant="soft" sx={{ mt: 2 }}>
                <CardContent>
                    <Typography level="h4" sx={{ mb: 2 }}>
                        Детальная статистика за {formatDate(selectedDay.date)}
                    </Typography>
                    
                    <Grid container spacing={2}>
                        <Grid xs={12} sm={6}>
                            <List>
                                <ListItem>
                                    <ListItemContent>
                                        <Typography level="body1" fontWeight="bold">
                                            Общая активность
                                        </Typography>
                                        <Chip color="primary" size="lg">
                                            {selectedDay.total_activity} действий
                                        </Chip>
                                    </ListItemContent>
                                </ListItem>
                                <ListItem>
                                    <ListItemContent>
                                        <Typography level="body2">Создано тестов:</Typography>
                                        <Typography level="title-md" color="primary">
                                            {selectedDay.tests_created}
                                        </Typography>
                                    </ListItemContent>
                                </ListItem>
                                <ListItem>
                                    <ListItemContent>
                                        <Typography level="body2">Завершено тестов:</Typography>
                                        <Typography level="title-md" color="success">
                                            {selectedDay.tests_completed}
                                        </Typography>
                                    </ListItemContent>
                                </ListItem>
                            </List>
                        </Grid>
                        <Grid xs={12} sm={6}>
                            <List>
                                <ListItem>
                                    <ListItemContent>
                                        <Typography level="body2">Отвечено вопросов:</Typography>
                                        <Typography level="title-md">
                                            {selectedDay.questions_answered}
                                        </Typography>
                                    </ListItemContent>
                                </ListItem>
                                <ListItem>
                                    <ListItemContent>
                                        <Typography level="body2">Правильных ответов:</Typography>
                                        <Typography level="title-md" color="success">
                                            {selectedDay.correct_answers}
                                        </Typography>
                                    </ListItemContent>
                                </ListItem>
                                <ListItem>
                                    <ListItemContent>
                                        <Typography level="body2">Неправильных ответов:</Typography>
                                        <Typography level="title-md" color="danger">
                                            {selectedDay.incorrect_answers}
                                        </Typography>
                                    </ListItemContent>
                                </ListItem>
                            </List>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        );
    };

    // Рендер мобильной версии
    const renderMobileVersion = () => {
        const months = getGroupedDataMobile();

        return (
            <Box sx={{ overflowX: 'auto' }}>
                <Stack spacing={1}>
                    {/* Заголовки дней недели */}
                    <Stack direction="row" spacing={0.5} sx={{ pl: 7.4 }}>
                        {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => (
                            <Typography 
                                key={dayIndex}
                                level="body3" 
                                color="neutral" 
                                sx={{ 
                                    width: 14,
                                    textAlign: 'center',
                                    fontSize: '10px',
                                    fontWeight: 'bold'
                                }}
                            >
                                {getWeekdayLabel(dayIndex)}
                            </Typography>
                        ))}
                    </Stack>

                    {/* Месяцы с неделями */}
                    {months.map((month, monthIndex) => (
                        <Stack key={monthIndex} direction="row" spacing={1}>
                            {/* Название месяца слева */}
                            <Typography 
                                level="body-sm" 
                                color="neutral" 
                                sx={{ 
                                    width: 50,
                                    textAlign: 'right',
                                    // fontSize: '11px',
                                    fontWeight: 'bold',
                                    lineHeight: '14px',
                                    pt: 0.5
                                }}
                            >
                                {month.name}
                            </Typography>
                            
                            {/* Недели месяца */}
                            <Stack spacing={0.5}>
                                {month.weeks.map((week, weekIndex) => (
                                    <Stack key={weekIndex} direction="row" spacing={0.5}>
                                        {week.map((day, dayIndex) => (
                                            <React.Fragment key={dayIndex}>
                                                {day ? (
                                                    <Tooltip
                                                        title={
                                                            <Box>
                                                                <Typography level="body2" fontWeight="bold">
                                                                    {getActivityDescription(day)}
                                                                </Typography>
                                                                <Typography level="body3">
                                                                    {formatDate(day.date)}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                        arrow
                                                        placement="top"
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 14,
                                                                height: 14,
                                                                borderRadius: '2px',
                                                                backgroundColor: getColorForLevel(day.level),
                                                                border: hoveredDay === day.date ? 
                                                                    '1px solid #1a1a1a' : 
                                                                    '1px solid rgba(27,31,35,0.06)',
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    border: '1px solid #1a1a1a',
                                                                    transform: 'scale(1.1)'
                                                                }
                                                            }}
                                                            onMouseEnter={() => setHoveredDay(day.date)}
                                                            onMouseLeave={() => setHoveredDay(null)}
                                                        />
                                                    </Tooltip>
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            width: 14,
                                                            height: 14,
                                                            borderRadius: '2px',
                                                            backgroundColor: 'transparent'
                                                        }}
                                                    />
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </Stack>
                                ))}
                            </Stack>
                        </Stack>
                    ))}
                </Stack>
            </Box>
        );
    }; 

    if (loading) {
        return (
            <Card variant="outlined">
                <CardContent>
                    <Typography level="h4" sx={{ mb: 2 }}>Активность</Typography>
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography level="body1" color="neutral">
                            Загрузка данных активности...
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card variant="outlined"sx={{ borderRadius: 'xl' }}>
            <CardContent>
                {/* Заголовок и управление */}
                <Stack 
                    direction={{ xs: 'column', sm: 'row' }} 
                    justifyContent="space-between" 
                    alignItems={{ xs: 'start', sm: 'center' }}
                    spacing={2}
                    sx={{ mb: 3 }}
                >
                    <Typography level="h4">Активность за {selectedYear} год</Typography>
                    
                    <Stack direction="row" spacing={2} alignItems="center">
                        {statistics && (
                            <Stack direction="row" spacing={1}>
                                <Chip variant="soft" color="primary" size={isMobile ? "sm" : "md"}>
                                    {statistics.total_tests} тестов
                                </Chip>
                                <Chip variant="soft" color="success" size={isMobile ? "sm" : "md"}>
                                    {statistics.total_answers} ответов
                                </Chip>
                            </Stack>
                        )}
                        
                        {/* Селект выбора года */}
                        <Select
                            value={selectedYear.toString()}
                            onChange={handleYearChange}
                            size={isMobile ? "sm" : "md"}
                            sx={{ minWidth: 100 }}
                        >
                            {availableYears.map(year => (
                                <Option key={year} value={year.toString()}>
                                    {year}
                                </Option>
                            ))}
                        </Select>
                    </Stack>
                </Stack>

                {/* Легенда */}
                <Stack 
                    direction="row" 
                    spacing={1} 
                    alignItems="center" 
                    sx={{ mb: 2, justifyContent: 'flex-end' }}
                >
                    <Typography level="body3" color="neutral">
                        Меньше
                    </Typography>
                    {[0, 1, 2, 3, 4].map(level => (
                        <Box
                            key={level}
                            sx={{
                                width: isMobile ? 8 : 12,
                                height: isMobile ? 8 : 12,
                                borderRadius: '2px',
                                backgroundColor: getColorForLevel(level),
                                border: '1px solid rgba(27,31,35,0.06)'
                            }}
                        />
                    ))}
                    <Typography level="body3" color="neutral">
                        Больше
                    </Typography>
                </Stack>

                {/* Тепловая карта */}
                <Box sx={{ overflowX: 'auto' }}>
                    {isMobile ? renderMobileVersion() : renderDesktopVersion()}
                </Box>

                {/* Информация о текущем дне */}
                {hoveredDay && (
                    <Box sx={{ mt: 2, p: 1, backgroundColor: 'background.level1', borderRadius: 'sm' }}>
                        <Typography level="body2">
                            <strong>{getActivityDescription(
                                heatmapData.find(d => d.date === hoveredDay)
                            )}</strong> — {formatDate(hoveredDay)}
                        </Typography>
                    </Box>
                )}

                {/* Детальная информация о выбранном дне */}
                {renderSelectedDayDetails()}

                {/* Общая статистика */}
                {statistics && (
                    <Stack 
                        direction="row" 
                        spacing={3} 
                        sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}
                        flexWrap="wrap"
                    >
                        <Box>
                            <Typography level="body3" color="neutral">
                                Создано тестов
                            </Typography>
                            <Typography level="title-sm" color="primary">
                                {statistics.total_tests_created}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography level="body3" color="neutral">
                                Завершено тестов
                            </Typography>
                            <Typography level="title-sm" color="success">
                                {statistics.total_tests_completed}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography level="body3" color="neutral">
                                Отвечено вопросов
                            </Typography>
                            <Typography level="title-sm">
                                {statistics.total_questions_answered}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography level="body3" color="neutral">
                                Правильных ответов
                            </Typography>
                            <Typography level="title-sm" color="success">
                                {statistics.total_correct_answers}
                            </Typography>
                        </Box>
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
};

export default ActivityHeatmap;